/**
 * Weekday welcome popup (sections/weekday-welcome-popup.liquid).
 *
 * Step 1: scratch card (canvas, 'destination-out' brush) or "Tap to reveal".
 * Step 2: Shopify customer form. It submits natively (never intercepted) so
 *         Shopify's hCaptcha can attach its token. Shopify returns the visitor
 *         to the same page with the form's anchor, and the result is read from
 *         the form state rendered inside the {% form %} tag.
 * Step 3: success, with the discount code applied in the background.
 *
 * Shown once, `data-delay` seconds after load. Closing or "No thanks" snoozes it
 * for 14 days; signing up hides it for good. Never shown on cart, checkout,
 * account or password pages. In the theme editor it opens when its section is
 * selected and never reads or writes storage.
 *
 * Offer tab (snippets/wk-offer-tab.liquid): reopens the popup at step 1 at any
 * time, ignoring the delay and the 14-day rule. Its "×" hides it for 7 days.
 * It is hidden while the popup is open, after sign-up, and on the same pages
 * the popup never shows on.
 */

const STORAGE_KEY = 'wk_welcome_popup';
const TAB_KEY = 'wk_welcome_tab';
const SUBMITTED_KEY = 'wk_welcome_popup_submitted';
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;
const TAB_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const CLEAR_THRESHOLD = 0.45;
const BRUSH = 44;
const BLOCKED_PATH = /^\/(?:[a-z]{2}(?:-[a-z]{2,4})?\/)?(?:cart|checkouts?|account|password)(?:\/|$)/i;

const readState = () => {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}') || {};
  } catch (error) {
    return {};
  }
};

const writeState = (state) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    /* storage unavailable: the popup simply may show again */
  }
};

const tabDismissed = () => {
  try {
    const state = JSON.parse(window.localStorage.getItem(TAB_KEY) || '{}') || {};
    return Boolean(state.hideUntil && state.hideUntil > Date.now());
  } catch (error) {
    return false;
  }
};

const dismissTab = () => {
  try {
    window.localStorage.setItem(TAB_KEY, JSON.stringify({ hideUntil: Date.now() + TAB_SNOOZE_MS }));
  } catch (error) {
    /* storage unavailable: the tab simply shows again on the next page */
  }
};

/** Remembers (for this tab) which form was just submitted; returns and clears it on read. */
const markSubmitted = (formId) => {
  try {
    window.sessionStorage.setItem(SUBMITTED_KEY, formId);
  } catch (error) {
    /* no-op: the return anchor alone still identifies the form */
  }
};

const takeSubmitted = () => {
  try {
    const formId = window.sessionStorage.getItem(SUBMITTED_KEY);
    window.sessionStorage.removeItem(SUBMITTED_KEY);
    return formId;
  } catch (error) {
    return null;
  }
};

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

class WkWelcomePopup extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.dialog = this.querySelector('[data-wk-dialog]');
    if (!(this.dialog instanceof HTMLDialogElement) || typeof this.dialog.showModal !== 'function') return;

    this.designMode = this.hasAttribute('data-design-mode');
    this.sectionId = this.dataset.sectionId;
    this.steps = {
      scratch: this.querySelector('[data-wk-step="scratch"]'),
      email: this.querySelector('[data-wk-step="email"]'),
      success: this.querySelector('[data-wk-step="success"]'),
    };
    this.labels = {
      scratch: `${this.dialog.id}-headline`,
      email: `${this.dialog.id}-offer`,
      success: `${this.dialog.id}-success`,
    };
    this.currentStep = 'scratch';
    this.revealed = false;
    this.form = this.querySelector('form');

    this.bindEvents();
    this.bindTab();

    if (this.designMode) {
      this.bindEditorEvents();
      this.updateTab();
      return;
    }

    if (BLOCKED_PATH.test(window.location.pathname)) return;
    this.tabAllowed = true;
    this.updateTab();

    // Back from submitting this popup's form: open straight on the result,
    // ignoring the delay and the 14-day rule.
    const result = this.readSubmitResult();
    if (result.ok) {
      this.onSignedUp();
      this.open('success');
      return;
    }
    if (result.error) {
      this.open('email');
      this.showError(result.error);
      return;
    }

    const saved = readState();
    if (saved.signedUp || (saved.snoozeUntil && saved.snoozeUntil > Date.now())) return;

    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    this.timer = window.setTimeout(() => this.open('scratch'), delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    if (this.editorAbort) this.editorAbort.abort();
    if (this.mediaQuery && this.onMediaChange) this.mediaQuery.removeEventListener('change', this.onMediaChange);
    if (this.dialog?.open) {
      this.dialog.close();
      this.unlockScroll();
    }
  }

  // ---------------------------------------------------------------- events

  bindEvents() {
    this.querySelectorAll('[data-wk-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });

    this.dialog.addEventListener('keydown', (event) => this.trapFocus(event));

    this.querySelector('[data-wk-reveal]')?.addEventListener('click', () => this.reveal());
    this.querySelector('[data-wk-to-email]')?.addEventListener('click', () => this.goTo('email'));
    this.querySelector('[data-wk-copy]')?.addEventListener('click', (event) => this.copyCode(event.currentTarget));

    // Never intercepted: only notes which form was sent, then lets the native
    // submit (and Shopify's hCaptcha) carry on.
    this.form?.addEventListener('submit', () => {
      if (!this.designMode && this.form) markSubmitted(this.form.id);
    });
  }

  bindEditorEvents() {
    this.editorAbort = new AbortController();
    const { signal } = this.editorAbort;
    const matches = (event) => event.detail?.sectionId === this.sectionId;

    document.addEventListener('shopify:section:select', (event) => matches(event) && this.open('scratch'), { signal });
    document.addEventListener('shopify:section:deselect', (event) => matches(event) && this.close(), { signal });
  }

  // ---------------------------------------------------------------- offer tab

  bindTab() {
    this.tab = this.querySelector('[data-wk-tab]');
    if (!this.tab) return;

    this.tab.querySelector('[data-wk-tab-open]')?.addEventListener('click', () => this.open('scratch'));
    this.tab.querySelector('[data-wk-tab-dismiss]')?.addEventListener('click', () => {
      if (!this.designMode) dismissTab();
      this.tabClosed = true;
      this.updateTab();
    });
  }

  /** The editor always shows the tab (and never reads storage) so it can be styled and tested. */
  updateTab() {
    if (!this.tab) return;
    let show = !this.dialog.open && !this.tabClosed;
    if (show && !this.designMode) show = Boolean(this.tabAllowed) && !readState().signedUp && !tabDismissed();
    this.tab.hidden = !show;
  }

  // ---------------------------------------------------------------- open/close

  open(step) {
    if (this.dialog.open) return;
    window.clearTimeout(this.timer);

    this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.lockScroll();
    this.dialog.showModal();
    this.showStep(step, false);
    this.updateTab();

    // "Show once": seeing it counts, even if the visitor navigates away.
    if (!this.designMode && !readState().signedUp) writeState({ snoozeUntil: Date.now() + SNOOZE_MS });

    if (step === 'scratch') {
      this.setupScratch();
      this.dialog.querySelector('[data-wk-close]')?.focus();
    } else if (step === 'email') {
      this.querySelector('input[type="email"]')?.focus();
    } else if (step === 'success') {
      this.steps.success?.querySelector('[tabindex="-1"]')?.focus();
    }
  }

  close() {
    if (!this.dialog.open) return;
    this.dialog.close();
    this.unlockScroll();

    if (!this.designMode && !readState().signedUp) writeState({ snoozeUntil: Date.now() + SNOOZE_MS });
    this.updateTab();

    if (this.previousFocus?.isConnected) this.previousFocus.focus();
    this.previousFocus = null;
  }

  lockScroll() {
    const html = document.documentElement;
    this.savedOverflow = { html: html.style.overflow, body: document.body.style.overflow };
    html.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  unlockScroll() {
    if (!this.savedOverflow) return;
    document.documentElement.style.overflow = this.savedOverflow.html;
    document.body.style.overflow = this.savedOverflow.body;
    this.savedOverflow = null;
  }

  trapFocus(event) {
    if (event.key !== 'Tab') return;
    const focusables = [...this.dialog.querySelectorAll(FOCUSABLE)].filter(
      (el) => !el.closest('[hidden]') && el.getClientRects().length > 0
    );
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // ---------------------------------------------------------------- steps

  showStep(step, animate) {
    Object.entries(this.steps).forEach(([name, el]) => {
      if (!el) return;
      el.hidden = name !== step;
      el.classList.remove('is-fading', 'is-entering');
    });
    const target = this.steps[step];
    if (animate && target && !prefersReducedMotion()) {
      target.classList.add('is-entering');
      target.addEventListener('animationend', () => target.classList.remove('is-entering'), { once: true });
    }
    this.currentStep = step;
    this.dialog.setAttribute('aria-labelledby', this.labels[step]);
  }

  goTo(step) {
    const from = this.steps[this.currentStep];
    const finish = () => {
      this.showStep(step, true);
      if (step === 'email') this.querySelector('input[type="email"]')?.focus();
      if (step === 'success') this.steps.success?.querySelector('[tabindex="-1"]')?.focus();
    };

    if (!from || from.hidden || prefersReducedMotion()) return finish();
    from.classList.add('is-fading');
    window.setTimeout(finish, 280);
  }

  // ---------------------------------------------------------------- scratch

  setupScratch() {
    if (this.revealed) return;
    this.canvas = this.querySelector('[data-wk-canvas]');
    if (!(this.canvas instanceof HTMLCanvasElement)) return;

    this.paintScratch();

    if (!this.scratchBound) {
      this.scratchBound = true;
      this.bindScratch();

      // The card changes size at 750px; repaint if nothing has been revealed yet.
      this.mediaQuery = window.matchMedia('(min-width: 750px)');
      this.onMediaChange = () => {
        if (!this.revealed && this.dialog.open) this.paintScratch();
      };
      this.mediaQuery.addEventListener('change', this.onMediaChange);

      // Repaint once web fonts are ready so "SCRATCH HERE" uses Inter.
      document.fonts?.ready.then(() => {
        if (!this.revealed && !this.hasScratched) this.paintScratch();
      });
    }
  }

  paintScratch() {
    const canvas = this.canvas;
    const rect = canvas.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));
    const ratio = Math.min(window.devicePixelRatio || 1, 2);

    canvas.width = width * ratio;
    canvas.height = height * ratio;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.globalCompositeOperation = 'source-over';

    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, '#FFC933');
    gradient.addColorStop(1, '#F5A623');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Subtle noise.
    const specks = Math.round((width * height) / 14);
    for (let i = 0; i < specks; i += 1) {
      const light = Math.random() > 0.5;
      ctx.fillStyle = light
        ? `rgba(255, 255, 255, ${0.05 + Math.random() * 0.08})`
        : `rgba(120, 60, 0, ${0.03 + Math.random() * 0.06})`;
      ctx.fillRect(Math.random() * width, Math.random() * height, 1, 1);
    }

    const bodyFont = getComputedStyle(this).getPropertyValue('--font-body--family').trim() || 'Inter';
    ctx.font = `700 26px ${bodyFont}, Inter, sans-serif`;
    ctx.fillStyle = '#FFFFFF';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('SCRATCH HERE', width / 2, height / 2);

    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineWidth = BRUSH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    this.ctx = ctx;
    this.hasScratched = false;
  }

  bindScratch() {
    const canvas = this.canvas;
    let drawing = false;
    let last = null;
    let moves = 0;

    const point = (event) => {
      const rect = canvas.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    canvas.addEventListener('pointerdown', (event) => {
      if (this.revealed || !this.ctx) return;
      event.preventDefault();
      drawing = true;
      canvas.setPointerCapture?.(event.pointerId);
      last = point(event);
      this.ctx.beginPath();
      this.ctx.arc(last.x, last.y, BRUSH / 2, 0, Math.PI * 2);
      this.ctx.fill();
      this.hasScratched = true;
    });

    canvas.addEventListener('pointermove', (event) => {
      if (!drawing || this.revealed || !this.ctx) return;
      event.preventDefault();
      const next = point(event);
      this.ctx.beginPath();
      this.ctx.moveTo(last.x, last.y);
      this.ctx.lineTo(next.x, next.y);
      this.ctx.stroke();
      last = next;
      moves += 1;
      if (moves % 8 === 0) this.checkCleared();
    });

    const stop = () => {
      if (!drawing) return;
      drawing = false;
      last = null;
      this.checkCleared();
    };
    canvas.addEventListener('pointerup', stop);
    canvas.addEventListener('pointercancel', stop);
    canvas.addEventListener('pointerleave', stop);
  }

  checkCleared() {
    if (this.revealed || !this.ctx) return;
    const { width, height } = this.canvas;
    let data;
    try {
      data = this.ctx.getImageData(0, 0, width, height).data;
    } catch (error) {
      return;
    }

    const stride = 8;
    let cleared = 0;
    let total = 0;
    for (let y = 0; y < height; y += stride) {
      for (let x = 0; x < width; x += stride) {
        total += 1;
        if (data[(y * width + x) * 4 + 3] < 128) cleared += 1;
      }
    }
    if (total && cleared / total >= CLEAR_THRESHOLD) this.reveal();
  }

  reveal() {
    if (this.revealed) return;
    this.revealed = true;

    const canvas = this.canvas || this.querySelector('[data-wk-canvas]');
    if (canvas) {
      canvas.classList.add('is-clearing');
      window.setTimeout(() => {
        canvas.hidden = true;
      }, prefersReducedMotion() ? 0 : 600);
    }

    this.querySelector('[data-wk-offer-under]')?.removeAttribute('aria-hidden');
    const announce = this.querySelector('[data-wk-announce]');
    const offer = this.querySelector('[data-wk-offer-under]')?.textContent.trim();
    if (announce && offer) announce.textContent = offer;

    const revealLink = this.querySelector('[data-wk-reveal]');
    if (revealLink) revealLink.hidden = true;

    const next = this.querySelector('[data-wk-to-email]');
    if (next) {
      next.hidden = false;
      next.focus({ preventScroll: true });
    }
  }

  // ---------------------------------------------------------------- form

  /**
   * Result of this popup's form after Shopify sends the visitor back.
   *
   * The footer newsletter is also a customer form, and Shopify's form state is
   * per form type, so the result only counts as ours when the URL carries this
   * form's return anchor or this tab just submitted this form.
   */
  readSubmitResult() {
    const form = this.form;
    if (!form || !form.id) return {};

    const submittedId = takeSubmitted();
    const ours = window.location.hash === `#${form.id}` || submittedId === form.id;
    if (!ours) return {};

    const stateEl = form.querySelector('[data-wk-form-state]');
    const posted =
      stateEl?.getAttribute('data-posted') === 'true' || /[?&]customer_posted=true(?:&|$)/.test(window.location.search);
    const errors = (stateEl?.getAttribute('data-errors') || '').trim();

    let result = {};
    if (posted) result = { ok: true };
    // Already-subscribed emails still count as signed up.
    else if (errors) result = /taken|already|exists|subscribed/i.test(errors) ? { ok: true } : { error: errors };

    if (result.ok || result.error) this.cleanReturnUrl();
    return result;
  }

  /** Drops customer_posted and the anchor so a reload doesn't reopen the result. */
  cleanReturnUrl() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('customer_posted');
      url.hash = '';
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (error) {
      /* no-op */
    }
  }

  showError(message) {
    const el = this.querySelector('[data-wk-error]');
    if (el) el.textContent = message;
    const input = this.querySelector('input[type="email"]');
    if (input) {
      if (message) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }
  }

  onSignedUp() {
    if (!this.designMode) writeState({ signedUp: true });

    const url = this.dataset.discountUrl;
    if (!url) return;
    const root = window.Shopify?.routes?.root || '/';
    const redirect = encodeURIComponent(`${root}cart.js`);
    fetch(`${url}?redirect=${redirect}`, { credentials: 'same-origin' }).catch(() => {});
  }

  copyCode(button) {
    const code = this.querySelector('[data-wk-code]')?.textContent.trim();
    if (!code || !(button instanceof HTMLElement)) return;

    const done = () => {
      const label = button.textContent;
      button.textContent = 'Copied';
      window.setTimeout(() => {
        button.textContent = label;
      }, 1800);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(done, done);
    } else {
      done();
    }
  }
}

if (!customElements.get('wk-welcome-popup')) {
  customElements.define('wk-welcome-popup', WkWelcomePopup);
}
